import {
  Briefcase,
  CalendarCheck,
  IndianRupee,
  Palmtree,
  Timer,
} from "lucide-react";
import {
  designationLabel,
  formatCurrencyInr,
  formatProfileDate,
  formatDays,
  formatStatusLabel,
  leaveBalanceRemaining,
  leaveBalanceUsed,
} from "../employeeProfileUtils";
import { normalizeSalaryPayoutMode, PAYOUT_FULL_PAY } from "../../../Salary/SalaryPayoutModeField";

const KPI_CONFIG = [
  { key: "employment", label: "Employment Status", icon: Briefcase, accent: "ep-kpi--green" },
  { key: "attendance", label: "Attendance (This Month)", icon: CalendarCheck, accent: "ep-kpi--blue" },
  { key: "leave", label: "Leave Balance", icon: Palmtree, accent: "ep-kpi--amber" },
  { key: "payroll", label: "Salary Package", icon: IndianRupee, accent: "ep-kpi--violet" },
  { key: "tenure", label: "Tenure", icon: Timer, accent: "ep-kpi--sky" },
];

function SummaryCard({ label, icon: Icon, accent, primary, secondary, meter }) {
  return (
    <article className={`ep-kpi-card ${accent}`}>
      <div className="ep-kpi-card-top">
        <span className="ep-kpi-icon-wrap" aria-hidden>
          <Icon size={18} strokeWidth={2.2} />
        </span>
        <p className="ep-kpi-label">{label}</p>
      </div>
      <p className="ep-kpi-value">{primary}</p>
      {meter != null ? (
        <div className="ep-kpi-meter" role="presentation">
          <div className="ep-kpi-meter-fill" style={{ width: `${Math.min(100, Math.max(0, meter))}%` }} />
        </div>
      ) : null}
      {secondary ? <p className="ep-kpi-sub">{secondary}</p> : null}
    </article>
  );
}

export default function EmployeeSummaryCards({ employee, sections }) {
  const attendance = sections.get("attendanceSummary").data?.summary;
  const leaveBalances = sections.get("leaveSummary").data?.balances || [];
  const salary = sections.get("salary").data;

  const leaveUsed = leaveBalances.reduce((s, b) => s + (leaveBalanceUsed(b) || 0), 0);
  const leaveRemaining = leaveBalances.reduce((s, b) => s + (leaveBalanceRemaining(b) || 0), 0);
  const leaveTotal = leaveUsed + leaveRemaining;
  const leavePct = leaveTotal > 0 ? Math.round((leaveUsed / leaveTotal) * 100) : 0;

  const structure = salary?.structure;
  const calc = salary?.calculation;
  const breakdown = calc?.salary_breakdown || {};
  const fullPay = normalizeSalaryPayoutMode(calc?.salary_payout_mode) === PAYOUT_FULL_PAY;
  const ctc = structure?.ctc ?? employee?.annual_ctc;
  const gross = breakdown.gross_salary ?? calc?.gross_salary ?? calc?.gross ?? calc?.monthly_gross;
  const net = fullPay ? gross : (breakdown.net_salary ?? calc?.net_salary ?? calc?.net ?? calc?.monthly_net);

  const tenureMonths = employee?.date_of_joining
    ? Math.max(
        0,
        (new Date().getFullYear() - new Date(employee.date_of_joining).getFullYear()) * 12 +
          (new Date().getMonth() - new Date(employee.date_of_joining).getMonth())
      )
    : null;

  const cards = {
    employment: {
      primary: formatStatusLabel(employee?.status),
      secondary: employee?.date_of_joining
        ? `Since ${formatProfileDate(employee.date_of_joining)}`
        : designationLabel(employee),
    },
    attendance: {
      primary: attendance?.attendancePct != null ? `${attendance.attendancePct}%` : "—",
      secondary: attendance
        ? `${attendance.present} present • ${attendance.absent} absent • ${attendance.workingDays} days`
        : "Loading month summary…",
      meter: attendance?.attendancePct,
    },
    leave: {
      primary: leaveBalances.length ? `${formatDays(leaveUsed)} used` : "—",
      secondary: leaveBalances.length
        ? `${formatDays(leaveRemaining)} remaining • ${leaveBalances.length} type(s)`
        : "No leave balance loaded",
      meter: leaveBalances.length ? leavePct : null,
    },
    payroll: {
      primary: formatCurrencyInr(ctc),
      secondary:
        structure?.template_name || structure?.salary_template_name
          ? `${structure.template_name || structure.salary_template_name}${fullPay ? " · no deductions" : ""}`
          : fullPay && (gross || net)
            ? `Payable ${formatCurrencyInr(net ?? gross)} · no deductions`
            : gross || net
            ? `Gross ${formatCurrencyInr(gross)} • Net ${formatCurrencyInr(net)}`
            : "No salary package assigned",
    },
    tenure: {
      primary:
        tenureMonths != null ? `${Math.floor(tenureMonths / 12)}y ${tenureMonths % 12}m` : "—",
      secondary: employee?.employee_type || "—",
    },
  };

  return (
    <section className="ep-kpi-section" aria-label="Employee summary metrics">
      <div className="ep-kpi-row">
        {KPI_CONFIG.map(({ key, label, icon, accent }) => {
          const data = cards[key];
          return (
            <SummaryCard
              key={key}
              label={label}
              icon={icon}
              accent={accent}
              primary={data.primary}
              secondary={data.secondary}
              meter={data.meter}
            />
          );
        })}
      </div>
    </section>
  );
}
